import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import {
  capitalizeText,
  formatDate,
  transformPurchaseType,
} from "../../utils/EmpUtil";
import { FINANCE_PRODUCT_TYPE, PIPELINE } from "../../common/Constants";
import { getPurchaseRequestReasonLabel } from "../../shared/constants/CancellationReasonConstants";

export const LincolnDetailsCard = ({
  data,
  testId,
  columns = 6,
  showRowDividers = true,
}) => (
  <Box
    component="dl"
    data-testid={testId}
    sx={{
      m: 0,
      border: "1px solid #B2B2B2",
      borderRadius: "8px",
      backgroundColor: "#FFFFFF",
      overflow: "hidden",
    }}
  >
    {Array.from(
      { length: showRowDividers ? Math.ceil(data.length / columns) : 1 },
      (_, row) => (
        <Box
          key={row}
          sx={{
            p: 4,
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              sm: `repeat(${Math.min(data.length, columns, 2)}, minmax(0, 1fr))`,
              md: `repeat(${Math.min(data.length, columns)}, minmax(0, 1fr))`,
            },
            gap: 4,
            borderTop: row && showRowDividers ? "1px solid #B2B2B2" : "none",
          }}
        >
          {(showRowDividers
            ? data.slice(row * columns, (row + 1) * columns)
            : data
          ).map((item) => (
            <Box key={item.heading} sx={{ minWidth: 0 }}>
              <Typography component="dt" variant="caption" sx={{ mb: 1 }}>
                {item.heading}
              </Typography>
              <Typography
                component="dd"
                variant="body2"
                sx={{ m: 0, overflowWrap: "anywhere" }}
              >
                {item.content ?? "--"}
              </Typography>
            </Box>
          ))}
        </Box>
      )
    )}
  </Box>
);

LincolnDetailsCard.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      heading: PropTypes.node.isRequired,
      content: PropTypes.node,
    })
  ).isRequired,
  testId: PropTypes.string,
  columns: PropTypes.number,
  showRowDividers: PropTypes.bool,
};

export const extractPurchaseInfo = (content) => {
  const {
    acceptRejectStatus,
    purchaseType,
    purchaseRequestDealerAction,
    purchaseRequestDate,
    soldDate,
    stockId,
  } = content?.customerReservation?.purchaseInfo || {};

  return {
    acceptRejectStatus: capitalizeText(acceptRejectStatus) || "--",
    purchaseType: capitalizeText(purchaseType) || "--",
    dealerAcceptedDate:
      formatDate(purchaseRequestDealerAction?.dealerAcceptedDate) || "--",
    dealerActionUserId: purchaseRequestDealerAction?.dealerActionUserId || "--",
    purchaseRequestDate: formatDate(purchaseRequestDate) || "--",
    soldDate: formatDate(soldDate) || "--",
    stockId: stockId || "--",
    dealerRejectedReason:
      getPurchaseRequestReasonLabel(
        purchaseRequestDealerAction?.dealerRejectedReason
      ) || "--",
  };
};

// Both tabs read the same order fields; Lincoln Pricing omits extra status details.
export const getPurchaseRequestDetails = (
  content,
  t,
  { includeStatusDetails = true } = {}
) => {
  const reservation = content?.customerReservation;
  const purchaseInfo = extractPurchaseInfo(content);
  const status = (
    reservation?.purchaseInfo?.acceptRejectStatus || ""
  ).toLowerCase();
  const canceled = status === "canceled" || status === "cancelled";
  let purchaseType =
    reservation?.paymentType === FINANCE_PRODUCT_TYPE.THIRD_PARTY
      ? "Third Party Financing"
      : transformPurchaseType(purchaseInfo.purchaseType);
  if (reservation?.subJourneyType === PIPELINE) purchaseType = "--";

  return [
    {
      heading: t("pricing.purchaseRequestDetails.fields.0"),
      content: purchaseInfo.acceptRejectStatus,
      highlight: true,
      color: status === "accepted" ? "success" : canceled ? "error" : "info",
    },
    {
      heading: t("pricing.purchaseRequestDetails.fields.1"),
      content: purchaseType,
    },
    ...(includeStatusDetails && (status === "accepted" || canceled)
      ? [
          {
            heading: t(
              `pricing.purchaseRequestAccepted.fields.${canceled ? 1 : 0}`
            ),
            content: purchaseInfo.dealerAcceptedDate,
          },
        ]
      : []),
    {
      heading: t("pricing.purchaseRequestDetails.fields.2"),
      content: purchaseInfo.purchaseRequestDate,
    },
    {
      heading: t("pricing.purchaseRequestDetails.fields.3"),
      content: purchaseInfo.soldDate,
    },
    {
      heading: t("pricing.purchaseRequestDetails.fields.4"),
      content: purchaseInfo.stockId,
    },
    {
      heading: t("pricing.purchaseRequestDetails.fields.5"),
      content: purchaseInfo.dealerActionUserId,
    },
    ...(includeStatusDetails && canceled
      ? [
          {
            heading: t("pricing.purchaseRequestCanceled.fields.0"),
            content: purchaseInfo.dealerRejectedReason,
          },
        ]
      : []),
  ];
};
